import { useEffect, useState } from 'react'
import { Bell, Megaphone, Send, X, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { formatCount } from './types'
import type { BroadcastFormData, OrganizationOption, Reach } from './types'

interface Props {
  open: boolean
  data: BroadcastFormData
  selectedOrgs: OrganizationOption[]
  reach: Reach
  processing: boolean
  onClose: () => void
  onConfirm: () => void
}

type Tab = 'summary' | 'agent' | 'user'

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors ${
        active ? 'border-red-500 text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2.5 text-sm">
      <span className="w-[150px] flex-shrink-0 text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  )
}

export default function ReviewModal({ open, data, selectedOrgs, reach, processing, onClose, onConfirm }: Props) {
  const [tab, setTab] = useState<Tab>('summary')

  useEffect(() => {
    if (open) setTab('summary')
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null

  const title = data.title.trim() || '(sem título)'
  const channelsOn = data.channels.panel || data.channels.email
  const extraChannelsValue =
    [data.channels.panel ? 'Notificação no painel' : null, data.channels.email ? 'E-mail' : null]
      .filter(Boolean)
      .join(' + ') || 'Nenhum'
  const userCaption = [
    data.channels.panel ? 'exibida no painel do usuário' : null,
    data.channels.email ? 'enviada por e-mail' : null,
  ]
    .filter(Boolean)
    .join(' e ')
  const numericHeader = 'px-2 py-1.5 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground'

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-12"
      onMouseDown={(event) => {
        // Fechar apenas quando o clique COMEÇA no backdrop: soltar uma seleção
        // de texto iniciada dentro do modal não pode fechá-lo.
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Revisar comunicado"
        className="w-full max-w-[660px] rounded-xl border border-border bg-card shadow-2xl"
      >
        <div className="flex items-start gap-2.5 px-5 pt-4">
          <Megaphone className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold text-foreground">Revisar comunicado</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Você está prestes a enviar para {formatCount(reach.agents)}{' '}
              {reach.agents === 1 ? 'agente' : 'agentes'} ({formatCount(reach.sessions)}{' '}
              {reach.sessions === 1 ? 'sessão' : 'sessões'})
              {channelsOn && (
                <>
                  {' '}
                  e {formatCount(reach.users)} {reach.users === 1 ? 'usuário' : 'usuários'}
                </>
              )}{' '}
              em {formatCount(reach.orgs)} {reach.orgs === 1 ? 'organização' : 'organizações'}.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-0.5 border-b border-border px-5 pt-3">
          <TabButton active={tab === 'summary'} onClick={() => setTab('summary')}>
            Resumo
          </TabButton>
          <TabButton active={tab === 'agent'} onClick={() => setTab('agent')}>
            Visão do agente
          </TabButton>
          {channelsOn && (
            <TabButton active={tab === 'user'} onClick={() => setTab('user')}>
              Visão do usuário
            </TabButton>
          )}
        </div>

        <div className="max-h-[52vh] overflow-y-auto px-5 py-4">
          {tab === 'summary' && (
            <>
              <div className="mb-4 flex flex-col gap-2">
                <SummaryRow label="Título" value={title} />
                <SummaryRow label="Canal padrão" value="Agentes — todas as sessões, entrega imediata" />
                <SummaryRow label="Canais adicionais" value={extraChannelsValue} />
                <SummaryRow
                  label="Organizações"
                  value={data.org_scope === 'all' ? `Todas (${reach.orgs})` : `${reach.orgs} selecionadas`}
                />
              </div>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Organização
                    </th>
                    <th className={numericHeader}>Agentes</th>
                    <th className={numericHeader}>Sessões</th>
                    <th className={numericHeader}>Usuários</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrgs.map((org) => (
                    <tr key={org.id} className="border-b border-border/40 last:border-b-0">
                      <td className="px-2 py-1.5 text-foreground">{org.name}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-foreground">
                        {org.eligibleAgentsCount}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-foreground">
                        {org.activeSessionsCount + org.eligibleAgentsCount}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-foreground">
                        {channelsOn ? org.usersCount : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {tab === 'agent' && (
            <>
              <div className="whitespace-pre-wrap break-words rounded-lg border border-border bg-background px-4 py-3.5 font-mono text-xs leading-7 text-muted-foreground">
                <span className="font-semibold text-foreground">[COMUNICADO DA PLATAFORMA — Station Zero]</span>
                {/* Espelha Broadcast#agent_message: título em branco não gera linha. */}
                {data.title.trim() && (
                  <>
                    {'\n'}
                    <span className="font-semibold text-foreground">{data.title.trim()}</span>
                  </>
                )}
                {'\n\n'}
                {data.content.trim()}
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Zap className="h-3 w-3 flex-shrink-0" />
                Como a mensagem aparece no contexto da sessão — entrega imediata em todas as sessões de
                usuário do agente.
              </p>
            </>
          )}
          {tab === 'user' && (
            <>
              <div className="mx-auto flex max-w-[420px] gap-3 rounded-lg border border-border bg-background px-4 py-3.5">
                <span className="flex h-[34px] w-[34px] flex-shrink-0 items-center justify-center rounded-full bg-red-500/10 text-red-500">
                  <Megaphone className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex items-baseline gap-2 text-[13px] font-semibold text-foreground">
                    <span className="truncate">{data.title.trim() || 'Comunicado da plataforma'}</span>
                    <span className="flex-shrink-0 text-[11px] font-normal text-muted-foreground/70">agora</span>
                  </p>
                  <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-muted-foreground">
                    {data.content.trim()}
                  </p>
                </div>
              </div>
              <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                <Bell className="h-3 w-3 flex-shrink-0" />
                Notificação {userCaption}.
              </p>
            </>
          )}
        </div>

        <div className="flex items-center gap-2.5 border-t border-border px-5 py-3.5">
          <span className="mr-auto text-[11px] text-muted-foreground">O envio não pode ser desfeito</span>
          <Button variant="outline" size="sm" onClick={onClose}>
            Voltar
          </Button>
          <Button
            size="sm"
            disabled={processing}
            onClick={onConfirm}
            className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
          >
            <Send className="h-4 w-4" />
            {processing ? 'Enviando...' : 'Enviar comunicado'}
          </Button>
        </div>
      </div>
    </div>
  )
}
